import { useEffect, useRef, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { track } from "@/lib/analytics";

// Shared submit lifecycle for public forms (anti-spam timing, status, errors).
export function useLeadForm(endpoint, initial, validate) {
  const [values, setValues] = useState({ ...initial, hp: "", consent: false });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const started = useRef(Date.now());

  const set = (k, v) => {
    setValues((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    if (!values.consent) errs.consent = "Merci de cocher cette case pour envoyer votre demande.";
    setErrors(errs);
    if (Object.keys(errs).length) {
      const first = document.querySelector(`[name="${Object.keys(errs)[0]}"], [data-testid="field-${Object.keys(errs)[0]}"]`);
      first?.focus?.();
      return;
    }
    setStatus("sending");
    setServerError("");
    try {
      const payload = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v === "" ? null : v]));
      await api.post(endpoint, { ...payload, elapsed_ms: Date.now() - started.current });
      setStatus("sent");
      track("form_submit", { form: endpoint });
    } catch (err) {
      setStatus("error");
      setServerError(errorMessage(err));
    }
  };

  return { values, errors, status, serverError, set, submit };
}

export function SubmitButton({ status, children, testid }) {
  const sending = status === "sending";
  return (
    <button type="submit" disabled={sending} data-testid={testid} className="group inline-flex min-h-[52px] items-center gap-4 bg-ink py-2 pl-7 pr-3 text-[15px] font-semibold text-ivory transition-colors duration-300 hover:bg-accent disabled:opacity-70">
      <span>{sending ? "Envoi en cours…" : children}</span>
      <span className="grid h-9 w-9 place-items-center bg-ivory text-ink">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
      </span>
    </button>
  );
}

export function FormError({ message }) {
  if (!message) return null;
  return <p role="alert" data-testid="form-error" className="border-l-2 border-red-800 bg-red-50 px-4 py-3 text-[14px] text-red-900">{message}</p>;
}

export function Confirmation({ title, children, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ block: "center" });
    ref.current?.focus();
  }, []);
  return (
    <div ref={ref} tabIndex={-1} role="status" aria-live="polite" data-testid={testid} className="border border-ink/15 bg-paper p-8 md:p-12">
      <span className="grid h-12 w-12 place-items-center bg-accent text-white"><Check className="h-5 w-5" /></span>
      <h2 className="display mt-8 text-[2.2rem] leading-tight md:text-[2.8rem]">{title}</h2>
      <div className="mt-5 max-w-xl space-y-3 text-mute leading-relaxed">{children}</div>
    </div>
  );
}
